import { useState, useRef, useEffect } from 'react';
import './App.css';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import ExcelUploader from './loaders/ExcelUploader';
import GoogleSheetsLoader from './loaders/GoogleSheetsLoader';
import DataGrid from './components/DataGrid';
import BlockConfigForm from './components/BlockConfigForm';
import ExcelExporter, { saveToGoogleSheets } from './components/ExcelExporter';
import HtmlPreview from './components/HtmlPreview';
import BlockTabs from './components/BlockTabs';
import { Block } from './classes/Block';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VideoExporter from './components/VideoExporter';
import VideoSettings, { VideoSettings as VideoSettingsType, DEFAULT_VIDEO_SETTINGS } from './components/VideoSettings';
import { I18nProvider, useLingui } from '@lingui/react';
import { i18n } from './i18n';
import { t } from '@lingui/core/macro';
import AuthGate from "./components/AuthGate";
import NavBar from './components/NavBar';
import PngExporter from './components/PngExporter';
import { auth } from './firebase';
import { buildTableData } from './utils/excelTable';
import FontManagement from './components/FontManagement';
import { FontInfo } from './components/FontManagement';
import LogoManagement from './components/LogoManagement';
import { LogoInfo } from './components/LogoManagement';
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import { ProjectProvider, useProject } from './contexts/ProjectContext';
import ProjectSelector from './pages/ProjectSelector';
import Renders from './pages/Renders';

export interface RowData {
  [key: string]: string | number;
}

const defaultFonts: FontInfo[] = [
  { name: 'Arial', file: null, url: '', enabled: true },
  { name: 'Times New Roman', file: null, url: '', enabled: true },
  { name: 'Courier New', file: null, url: '', enabled: true },
  { name: 'Verdana', file: null, url: '', enabled: true },
  { name: 'Tahoma', file: null, url: '', enabled: true },
  { name: 'Georgia', file: null, url: '', enabled: true },
  { name: 'Trebuchet MS', file: null, url: '', enabled: true },
  { name: 'Impact', file: null, url: '', enabled: true },
  { name: 'Comic Sans MS', file: null, url: '', enabled: true },
  { name: 'Palatino Linotype', file: null, url: '', enabled: true },
  { name: 'Lucida Console', file: null, url: '', enabled: true },
  { name: 'Lucida Sans Unicode', file: null, url: '', enabled: true },
  { name: 'Garamond', file: null, url: '', enabled: true },
  { name: 'Century Gothic', file: null, url: '', enabled: true },
];

// Main app content component (the main view with blocks and data grid)
function MainAppContent({
  rowData,
  columnDefs,
  originalSheetName,
  firstColumnValues,
  blocks,
  selectedBlockIndex,
  isHTMLExportEnabled,
  showPreview,
  videoSettings,
  fonts,
  logos,
  googleSheetsUrl,
  isBlockPropertiesCollapsed,
  previewRef,
  handleFileUpload,
  handleSaveBlocks,
  handleRowDataChange,
  handleVideoSettingsChange,
  toggleFullscreen,
  setShowPreview,
  setSelectedBlockIndex,
  setBlocks,
  setIsBlockPropertiesCollapsed,
  setIsHTMLExportEnabled,
  setFonts,
  setLogos,
  setGoogleSheetsUrl,
  setVideoSettings,
}: any) {
  const navigate = useNavigate();
  const { selectedProject } = useProject();

  const getFilteredRowData = () => {
    if (selectedBlockIndex === null || selectedBlockIndex < 0 || selectedBlockIndex >= blocks.length) {
      return [];
    }

    const selectedBlock = blocks[selectedBlockIndex];
    return selectedBlock.dataRows || [];
  };

  const filteredRowData = getFilteredRowData();

  return (
    <div className="App">
      <NavBar />
      <main className="App-content">
        <div className="top-controls" style={{paddingTop: "3px"}}>
          <div className="button-group">
            <ExcelUploader onFileUpload={handleFileUpload} />
            <GoogleSheetsLoader 
              onFileUpload={handleFileUpload} 
              hasExistingData={rowData.length > 0}
              onSheetsUrlUpdate={setGoogleSheetsUrl}
            />
            {googleSheetsUrl && (
              <button 
                className="load-button"
                onClick={() => window.open(googleSheetsUrl, '_blank')}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}
              >
                <span style={{ marginRight: '8px' }}>📋</span>
                {t`Go to Google Sheets`}
                <span style={{ marginLeft: '8px' }}>↗</span>
              </button>
            )}
            <button 
              className="load-button"
              onClick={() => navigate('/app/renders')}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}
            >
              <span style={{ marginRight: '8px' }}>🎬</span>
              Renders
            </button>
          </div>
          {rowData.length > 0 && (
            <div className="settings-grid">
              <VideoSettings
                onSettingsChange={handleVideoSettingsChange}
                initialSettings={videoSettings}
              />
              <FontManagement
                onFontListChange={(updatedFonts) => {
                  setFonts(updatedFonts);
                  
                  // Save to Google Sheets when font list changes (use current videoSettings since fonts don't directly update videoSettings in this callback)
                  if (rowData.length > 0 && columnDefs.length > 0 && blocks.length > 0 && selectedProject?.id) {
                    (async () => {
                      try {
                        const table = buildTableData(blocks, columnDefs, videoSettings);
                        const userEmail: string | null = auth.currentUser?.email ?? null;
                        await saveToGoogleSheets(table, originalSheetName, userEmail, selectedProject.id);
                      } catch (error) {
                        console.error('Error saving font changes to Google Sheets:', error);
                      }
                    })();
                  }
                }}
                initialFonts={fonts}
                onFontUploaded={(fontName, serverUrl) => {
                  setVideoSettings((prev: VideoSettingsType) => ({
                    ...prev,
                    fonts: { ...(prev.fonts || {}), [fontName]: serverUrl }
                  }));
                }}
              />
              <LogoManagement
                onLogoListChange={(updatedLogos) => {
                  setLogos(updatedLogos);
                  // Update video settings with all logos (enabled and disabled)
                  const allLogosMap: Record<string, { url: string; isEnabled: boolean }> = {};
                  updatedLogos.forEach((logo: LogoInfo) => {
                    if (logo.url) {
                      allLogosMap[logo.name] = {
                        url: logo.url,
                        isEnabled: logo.enabled
                      };
                    }
                  });
                  const updatedVideoSettings = {
                    ...videoSettings,
                    logos: allLogosMap
                  };
                  setVideoSettings(updatedVideoSettings);
                  
                  // Save to Google Sheets when logo list changes
                  if (rowData.length > 0 && columnDefs.length > 0 && blocks.length > 0 && selectedProject?.id) {
                    (async () => {
                      try {
                        const table = buildTableData(blocks, columnDefs, updatedVideoSettings);
                        const userEmail: string | null = auth.currentUser?.email ?? null;
                        await saveToGoogleSheets(table, originalSheetName, userEmail, selectedProject.id);
                      } catch (error) {
                        console.error('Error saving logo changes to Google Sheets:', error);
                      }
                    })();
                  }
                }}
                initialLogos={logos}
                onLogoUploaded={(logoName, serverUrl) => {
                  setVideoSettings((prev: VideoSettingsType) => ({
                    ...prev,
                    logos: { 
                      ...(prev.logos || {}), 
                      [logoName]: { url: serverUrl, isEnabled: true }
                    }
                  }));
                }}
              />
            </div>
          )}
        </div>

        {rowData.length > 0 && firstColumnValues.length > 0 ? (
          <>
            <div className="main-content">
              <div className={`block-properties${isBlockPropertiesCollapsed ? ' collapsed' : ''}`} style={{ position: 'relative' }}>
                <div
                  className="collapse-arrow"
                  onClick={() => setIsBlockPropertiesCollapsed(!isBlockPropertiesCollapsed)}
                  aria-label={isBlockPropertiesCollapsed ? t`Expand Block Properties` : t`Collapse Block Properties`}
                >
                  {isBlockPropertiesCollapsed
                    ? (<ChevronRightIcon />)
                    : (<ChevronLeftIcon />)
                  }
                </div>
                {!isBlockPropertiesCollapsed && (
                  <>
                    <h2>{t`Block Properties`}</h2>
                    <BlockConfigForm
                      blockNames={firstColumnValues}
                      onSaveBlocks={handleSaveBlocks}
                      onBlockSelect={setSelectedBlockIndex}
                      selectedBlockIndex={selectedBlockIndex}
                      existingBlocks={blocks}
                      fontOptions={fonts.filter((f: FontInfo) => f.enabled).map((f: FontInfo) => ({ value: f.name, label: f.name }))}
                    />
                  </>
                )}
              </div>

              <BlockTabs
                blocks={blocks}
                selectedBlockIndex={selectedBlockIndex}
                onBlockSelect={setSelectedBlockIndex}
                onBlocksChange={setBlocks}
              />

              <div className="excel-data">
                <h2>{t`Excel Data & Credits`}</h2>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <button className="fancy-button" onClick={() => setShowPreview(!showPreview)}>
                    {showPreview ? t`Show Excel Data` : t`Show Preview`}
                  </button>

                  {showPreview && (
                    <>
                      <button className="fancy-button" onClick={toggleFullscreen} >
                        {t`Toggle Fullscreen`}
                      </button>

                      <div style={{paddingLeft: "5px", paddingBottom: "2px"}} className="tooltip-wrapper">
                        <span className="tooltip-icon">?</span>
                        <span className="tooltip-text">
                          {t`Fullscreen mode allows you to view the credits properly (without overlapping).`}
                        </span>
                      </div>
                    </>
                  )}
                </div>

                <div ref={previewRef}>
                  {selectedBlockIndex !== null && blocks.length > 0 && selectedBlockIndex < blocks.length ? (
                    columnDefs.length > 0 && filteredRowData.length > 0 ? (
                      showPreview ? (
                        <HtmlPreview
                          selectedBlocks={(() => {
                            const blocksToSend: Block[] = [];
                            const selectedBlock = blocks[selectedBlockIndex];

                            // Add previous block if not the first block
                            if (selectedBlockIndex > 0) {
                              blocksToSend.push(blocks[selectedBlockIndex - 1]);
                            }

                            // Add selected block
                            blocksToSend.push(selectedBlock);

                            // Add next block if not the last block
                            if (selectedBlockIndex < blocks.length - 1) {
                              blocksToSend.push(blocks[selectedBlockIndex + 1]);
                            }

                            return blocksToSend;
                          })()}
                          selectedBlockIndex={selectedBlockIndex === 0 ? 0 : 1}
                          videoSettings={videoSettings}
                          columnDefs={columnDefs}
                          blocks={blocks}
                          actualSelectedBlockIndex={selectedBlockIndex}
                          onBlockChange={async (blockIndex: number, updatedBlock: Block) => {
                            const updatedBlocks: Block[] = [...blocks];
                            updatedBlocks[blockIndex] = updatedBlock;
                            await handleSaveBlocks(updatedBlocks);
                          }}
                        />
                      ) : (
                        <>
                          <p>{t`Showing ${filteredRowData.length} rows associated with block: ${blocks[selectedBlockIndex].name}`}</p>
                          <DataGrid
                            rowData={filteredRowData}
                            columnDefs={columnDefs}
                            isLogoBlock={blocks[selectedBlockIndex].isLogoBlock}
                            isSingleColumn={blocks[selectedBlockIndex].isSingleCell}
                            isSongBlock={blocks[selectedBlockIndex].isSongBlock}
                            logoOptions={logos.filter((l: LogoInfo) => l.enabled).map((l: LogoInfo) => l.name)}
                            onRowDataChange={handleRowDataChange}
                          />
                        </>
                      )
                    ) : (
                      <p>{t`No data associated with the selected block.`}</p>
                    )
                  ) : (
                    <p>{t`Please select a block to view associated data.`}</p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <ExcelExporter
                    rowData={rowData}
                    columnDefs={columnDefs}
                    sheetName={originalSheetName}
                    blocks={blocks}
                    onExport={() => {
                      setIsHTMLExportEnabled(true);
                    }}
                    videoSettings={videoSettings}
                  />
                  <PngExporter
                    columnDefs={columnDefs}
                    sheetName={originalSheetName}
                    blocks={blocks}
                    isEnabled={true}
                    videoSettings={videoSettings}
                  />
                  <VideoExporter
                    columnDefs={columnDefs}
                    sheetName={originalSheetName}
                    blocks={blocks}
                    isEnabled={true}
                    videoSettings={videoSettings}
                  />
                </div>
              </div>
            </div>
          </>
        ) : (
          <p>{t`Please upload an Excel file to view and edit data.`}</p>
        )}
      </main>
    </div>
  );
}

// Wrapper component that maintains state and provides it to nested routes
function AppContent() {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { i18n: linguiI18n } = useLingui();
  const { selectedProject, setSelectedProject } = useProject();
  const location = useLocation();
  const [rowData, setRowData] = useState<RowData[]>([]);
  const [columnDefs, setColumnDefs] = useState<any[]>([]);
  const [originalSheetName, setOriginalSheetName] = useState<string>('');
  const [firstColumnValues, setFirstColumnValues] = useState<string[]>([]);
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [selectedBlockIndex, setSelectedBlockIndex] = useState<number | null>(null);
  const [isHTMLExportEnabled, setIsHTMLExportEnabled] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [videoSettings, setVideoSettings] = useState<VideoSettingsType>(DEFAULT_VIDEO_SETTINGS);

  const [fonts, setFonts] = useState<FontInfo[]>(defaultFonts);
  const [logos, setLogos] = useState<LogoInfo[]>([]);
  const [googleSheetsUrl, setGoogleSheetsUrl] = useState<string | null>(null);

  const previewRef = useRef<HTMLDivElement>(null);
  const [isBlockPropertiesCollapsed, setIsBlockPropertiesCollapsed] = useState<boolean>(false);
  const prevProjectIdRef = useRef<string | undefined>(undefined);

  // Reset all state when project changes (but not on initial mount with same project)
  useEffect(() => {
    const currentProjectId: string | undefined = selectedProject?.id;
    const previousProjectId: string | undefined = prevProjectIdRef.current;
    
    // Only reset if project ID actually changed (not just on remount)
    if (previousProjectId !== undefined && previousProjectId !== currentProjectId) {
      setRowData([]);
      setColumnDefs([]);
      setOriginalSheetName('');
      setFirstColumnValues([]);
      setBlocks([]);
      setSelectedBlockIndex(null);
      setIsHTMLExportEnabled(false);
      setShowPreview(false);
      setVideoSettings(DEFAULT_VIDEO_SETTINGS);
      setFonts(defaultFonts);
      setLogos([]);
      setGoogleSheetsUrl(null);
    }
    
    // Update the ref to track current project ID
    prevProjectIdRef.current = currentProjectId;
  }, [selectedProject?.id]);

  const handleFileUpload = (
    allSheetDataRows: RowData[],
    columnsFromUploader: any[],
    sheetName: string,
    extractedBlocksFromUploader?: Block[],
    videoSettingsFromFile?: VideoSettingsType
  ) => {
    setRowData([]);
    setColumnDefs([]);
    setOriginalSheetName('');
    setFirstColumnValues([]);
    setBlocks([]);
    setSelectedBlockIndex(null);

    if (allSheetDataRows.length === 0) {
      console.error('No data rows found in the uploaded file');
      return;
    }

    const formattedColumns = columnsFromUploader && columnsFromUploader.length > 0
      ? columnsFromUploader.map(colDefFromUploader => ({
          field: colDefFromUploader.field,
          headerName: colDefFromUploader.headerName,
          flex: colDefFromUploader.flex === undefined ? 1 : colDefFromUploader.flex,
          sortable: colDefFromUploader.sortable === undefined ? true : colDefFromUploader.sortable,
          filter: colDefFromUploader.filter === undefined ? true : colDefFromUploader.filter,
          resizable: colDefFromUploader.resizable === undefined ? true : colDefFromUploader.resizable,
        }))
      : [];

    const blockNameSourceField = 'col0';
    const uniqueFirstColValues = Array.from(new Set(
      allSheetDataRows
        .map(row => String(row[blockNameSourceField] || '').trim())
        .filter(name => name && name !== "undefined")
    ));

    setRowData(allSheetDataRows);
    setColumnDefs(formattedColumns);
    setOriginalSheetName(sheetName);
    setFirstColumnValues(uniqueFirstColValues);

    let newBlocksToSet: Block[] = [];
    if (extractedBlocksFromUploader && extractedBlocksFromUploader.length > 0) {
      newBlocksToSet = extractedBlocksFromUploader.filter(block => block.name && block.name.trim() !== '' && block.name !== "undefined");
    } else {
      newBlocksToSet = uniqueFirstColValues.map(name => new Block(
          name,
          true, 'Arial', 30, 400, '#000000',
          1, 5, 45, 0, 1, 10,
          false, false, false, "•", false,
          'Arial', 11, 400, "right", "bottom", '#000000',
          'Arial', 11, 400, "left", "bottom", '#000000',
          false,
          []
        ));
    }

    setBlocks(newBlocksToSet);
    setSelectedBlockIndex(newBlocksToSet.length > 0 ? 0 : null);

    const vs: VideoSettingsType = videoSettingsFromFile || {
      ...DEFAULT_VIDEO_SETTINGS,
      video_time: allSheetDataRows.length * 3
    };
    setVideoSettings(vs);

    // Merge default fonts + fonts from video settings into FontManagement
    try {
      const fontsFromSettings = vs.fonts || {};
      const mergedFonts = [
        ...defaultFonts,
        ...Object.keys(fontsFromSettings).map(name => ({
          name,
          file: null,
          url: fontsFromSettings[name],
          enabled: true,
        }))
      ];
      setFonts(mergedFonts);
    } catch (e) {
      setFonts(defaultFonts);
    }

    // Initialize logos from video settings into LogoManagement
    try {
      const logosFromSettings = (vs as any).logos || {};
      const logoKeys = Object.keys(logosFromSettings);
      
      // Check if logos data is empty (empty object, empty string, or no valid logos)
      const isEmptyLogos = logoKeys.length === 0 || 
        (logoKeys.length === 1 && logoKeys[0] === '' && !logosFromSettings['']);
      
      if (isEmptyLogos) {
        // If logos are empty, fetch available logos and populate them
        setLogos([]); // Start with empty array, LogoManagement will populate with available logos
      } else {
        const initialLogos: LogoInfo[] = logoKeys.map(name => {
          const logoData = logosFromSettings[name];
          // Handle both old format (string) and new format (object)
          if (typeof logoData === 'string') {
            return {
              name,
              file: null,
              url: logoData,
              enabled: true,
            };
          } else if (logoData && typeof logoData === 'object') {
            return {
              name,
              file: null,
              url: logoData.url || '',
              enabled: logoData.isEnabled !== undefined ? logoData.isEnabled : true,
            };
          }
          return null;
        }).filter(logo => logo !== null) as LogoInfo[];
        setLogos(initialLogos);
      }
    } catch (e) {
      setLogos([]);
    }
  };

  const handleSaveBlocks = async (newBlocks: Block[]) => {
    const blocksChanged = newBlocks.length !== blocks.length ||
      newBlocks.some((newBlock, index) => {
        const oldBlock = blocks[index];
        return JSON.stringify(newBlock) !== JSON.stringify(oldBlock);
      });

    if (blocksChanged) {
      setBlocks(newBlocks);

      // Save to Google Sheets when blocks change
      if (rowData.length > 0 && columnDefs.length > 0 && selectedProject?.id) {
        try {
          const table = buildTableData(newBlocks, columnDefs, videoSettings);
          const userEmail: string | null = auth.currentUser?.email ?? null;
          await saveToGoogleSheets(table, originalSheetName, userEmail, selectedProject.id);
        } catch (error) {
          console.error('Error saving blocks to Google Sheets:', error);
        }
      }
    }
  };

  const handleRowDataChange = async (newRowData: RowData[]) => {
    if (selectedBlockIndex === null || selectedBlockIndex < 0 || selectedBlockIndex >= blocks.length) {
      return;
    }

    const updatedBlocks = [...blocks];
    updatedBlocks[selectedBlockIndex] = {
      ...updatedBlocks[selectedBlockIndex],
      dataRows: newRowData
    };
    setBlocks(updatedBlocks);

    // Save to Google Sheets when row data changes
    if (rowData.length > 0 && columnDefs.length > 0 && selectedProject?.id) {
      try {
        const table = buildTableData(updatedBlocks, columnDefs, videoSettings);
        const userEmail: string | null = auth.currentUser?.email ?? null;
        await saveToGoogleSheets(table, originalSheetName, userEmail, selectedProject.id);
      } catch (error) {
        console.error('Error saving row data to Google Sheets:', error);
      }
    }
  };

  const handleVideoSettingsChange = async (newSettings: VideoSettingsType) => {
    setVideoSettings(newSettings);

    // Save to Google Sheets when video settings change
    if (rowData.length > 0 && columnDefs.length > 0 && blocks.length > 0 && selectedProject?.id) {
      try {
        const table = buildTableData(blocks, columnDefs, newSettings);
        const userEmail: string | null = auth.currentUser?.email ?? null;
        await saveToGoogleSheets(table, originalSheetName, userEmail, selectedProject.id);
      } catch (error) {
        console.error('Error saving video settings to Google Sheets:', error);
      }
    }
  };

  const toggleFullscreen = () => {
    const elem = previewRef.current;
    if (!elem) return;

    const htmlPreview = document.getElementById("html-preview");
    const previewFrame = document.getElementById("preview-frame");

    const isFullscreen = document.fullscreenElement;

    if (!isFullscreen) {
      previewFrame?.classList.replace('preview-frame', 'preview-fullscreen');
      htmlPreview?.classList.replace("html-preview", "html-preview-fullscreen");

      elem.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable full-screen mode: ${err.message}`);
      });
    } else {
      previewFrame?.classList.replace('preview-fullscreen', 'preview-frame');
      htmlPreview?.classList.replace("html-preview-fullscreen", "html-preview");
      document.exitFullscreen();
    }
  };

  // Auto-fix UI when user hits ESC to exit fullscreen manually
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        document.getElementById("preview-frame")?.classList.replace('preview-fullscreen', 'preview-frame');
        document.getElementById("html-preview")?.classList.replace("html-preview-fullscreen", "html-preview");
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Show project selector if no project is selected
  if (!selectedProject) {
    return <ProjectSelector onProjectSelect={setSelectedProject} />;
  }

  // Render nested routes (either main app content or renders page)
  // Check if we're on the renders route
  const isRendersRoute = location.pathname === '/app/renders';

  if (isRendersRoute) {
    return (
      <div style={{ paddingTop: '60px' }}>
        <NavBar />
        <Renders />
      </div>
    );
  }

  // Otherwise render the main app content
  return (
    <MainAppContent
      rowData={rowData}
      columnDefs={columnDefs}
      originalSheetName={originalSheetName}
      firstColumnValues={firstColumnValues}
      blocks={blocks}
      selectedBlockIndex={selectedBlockIndex}
      isHTMLExportEnabled={isHTMLExportEnabled}
      showPreview={showPreview}
      videoSettings={videoSettings}
      fonts={fonts}
      logos={logos}
      googleSheetsUrl={googleSheetsUrl}
      isBlockPropertiesCollapsed={isBlockPropertiesCollapsed}
      previewRef={previewRef}
      handleFileUpload={handleFileUpload}
      handleSaveBlocks={handleSaveBlocks}
      handleRowDataChange={handleRowDataChange}
      handleVideoSettingsChange={handleVideoSettingsChange}
      toggleFullscreen={toggleFullscreen}
      setShowPreview={setShowPreview}
      setSelectedBlockIndex={setSelectedBlockIndex}
      setBlocks={setBlocks}
      setIsBlockPropertiesCollapsed={setIsBlockPropertiesCollapsed}
      setIsHTMLExportEnabled={setIsHTMLExportEnabled}
      setFonts={setFonts}
      setLogos={setLogos}
      setGoogleSheetsUrl={setGoogleSheetsUrl}
      setVideoSettings={setVideoSettings}
    />
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged((user) => {
      setIsAuthenticated(!!user);
    });
    return () => unsub();
  }, []);
  
  // NOTE: These wrappers are used inside the Router (provided by index.tsx)
  const LandingWrapper = () => {
    const navigate = useNavigate();
    return <Landing onLogin={() => navigate('/login')} />;
  };

  const LoginWrapper = () => {
    // If user already authenticated, redirect to app
    if (isAuthenticated) return <Navigate to="/app" replace />;
    return <Login />;
  };

  const AppWrapper = () => {
    // If we don't know auth state yet, avoid rendering (prevents flash)
    if (isAuthenticated === null) return null;
    // Protect /app route using AuthGate (assumes AuthGate will redirect to /login if not auth)
    return (
      <AuthGate>
        <AppContent />
      </AuthGate>
    );
  };

  return (
    <I18nProvider i18n={i18n}>
      <ProjectProvider>
        {/* IMPORTANT: your project should provide a single BrowserRouter at the root.
            Typically index.tsx contains: <BrowserRouter><App /></BrowserRouter>
            This component assumes the Router is already active. */}
        <Routes>
          {/* Landing page */}
          <Route path="/" element={isAuthenticated ? <Navigate to="/app" replace /> : <LandingWrapper />} />

          {/* Login - if already authed, redirect to /app */}
          <Route path="/login" element={<LoginWrapper />} />

          {/* Main app route (protected by AuthGate) - now handles both /app and /app/renders */}
          <Route path="/app" element={<AppWrapper />} />
          <Route path="/app/renders" element={<AppWrapper />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ProjectProvider>
    </I18nProvider>
  );
}

export default App;